import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

const API = process.env.REACT_APP_BACKEND_URL;

const HeadlineTicker = () => {
  const [items, setItems] = useState([]);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const res = await fetch(`${API}/api/ticker`);
        const admin = res.ok ? await res.json() : [];
        if (!cancelled && Array.isArray(admin) && admin.length > 0) {
          setItems(admin.map((h) => ({ key: h.id, title: h.text, link: h.link || '/stories' })));
          return;
        }
        // Fall back to the latest native articles
        const local = await fetch(`${API}/api/articles?limit=8`);
        const data = local.ok ? await local.json() : { items: [] };
        const list = Array.isArray(data.items) ? data.items : [];
        if (!cancelled) {
          setItems(list.slice(0, 8).map((s, i) => ({
            key: s.slug || `art-${i}`,
            title: s.title,
            link: `/stories/${s.slug}`,
          })));
        }
      } catch (err) {
        console.error('Ticker fetch failed:', err);
      }
    };
    load();
    const interval = setInterval(load, 60000);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, []);

  if (items.length === 0) {
    // Reserve the ticker's height even before items load to prevent the rest
    // of the page from shoving down when the API returns (kills ~0.2 CLS).
    return (
      <div
        className="w-full bg-black border-y border-red-600/40"
        style={{ minHeight: 32 }}
        aria-hidden="true"
        data-testid="headline-ticker-placeholder"
      />
    );
  }

  const renderItem = (s, idx) => {
    const isExternal = s.link && /^https?:\/\//i.test(s.link);
    const inner = (
      <>
        <span className="text-gray-200 hover:text-red-300 transition-colors">{s.title}</span>
        <span className="mx-2 text-red-500/80">●</span>
      </>
    );
    return isExternal ? (
      <a
        key={`${s.key}-${idx}`}
        href={s.link}
        target="_blank"
        rel="noopener noreferrer"
        data-testid={`ticker-item-${idx}`}
        className="text-xs md:text-sm font-bold tracking-wide"
      >
        {inner}
      </a>
    ) : (
      <Link
        key={`${s.key}-${idx}`}
        to={s.link || '/stories'}
        data-testid={`ticker-item-${idx}`}
        className="text-xs md:text-sm font-bold tracking-wide"
      >
        {inner}
      </Link>
    );
  };

  // Build TWO identical copies side by side. The marquee shifts by exactly
  // the width of one copy (-50%), so when the animation resets, the second
  // copy is sitting precisely where the first one started - visually seamless.
  return (
    <div
      className="relative w-full bg-black border-y border-red-600/40 overflow-hidden"
      data-testid="headline-ticker"
    >
      <div className="flex items-stretch">
        <span className="shrink-0 bg-red-600 text-white text-[10px] font-black tracking-[0.2em] px-3 flex items-center">
          LIVE
        </span>
        <div className="relative flex-1 overflow-hidden">
          <div
            className="ticker-track whitespace-nowrap py-1.5 will-change-transform"
            style={{ animationDuration: `${Math.max(items.length * 11.5, 32)}s` }}
          >
            <span className="ticker-half" data-testid="ticker-half-a">
              {items.map(renderItem)}
            </span>
            <span className="ticker-half" aria-hidden="true">
              {items.map((s, i) => renderItem(s, i + items.length))}
            </span>
          </div>
          <div className="absolute inset-y-0 left-0 w-8 bg-gradient-to-r from-black to-transparent pointer-events-none"></div>
          <div className="absolute inset-y-0 right-0 w-8 bg-gradient-to-l from-black to-transparent pointer-events-none"></div>
        </div>
      </div>

      <style>{`
        .ticker-track {
          display: inline-flex;
          width: max-content;
          animation-name: ticker-scroll;
          animation-timing-function: linear;
          animation-iteration-count: infinite;
        }
        .ticker-track:hover { animation-play-state: paused; }
        .ticker-half {
          display: inline-flex;
          flex-shrink: 0;
          align-items: center;
        }
        @keyframes ticker-scroll {
          from { transform: translate3d(0, 0, 0); }
          to   { transform: translate3d(-50%, 0, 0); }
        }
      `}</style>
    </div>
  );
};

export default HeadlineTicker;
